import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/Button";
import DeleteModal from "../../../components/modals/DeleteModal";
import {
    getAllSoundLibraryAICategories,
    createSoundLibraryAICategory,
    updateSoundLibraryAICategory,
    deleteSoundLibraryAICategory,
} from "../../../api/soundLibrary";
import { SoundLibraryAICategoryResponse } from "../../../types/requestsOutputs";
import { useLanguage } from "../../../context/LanguageContext";
import { useNotification } from "../../../context/NotificationContext";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import FormGroup from "../../../components/common/FormGroup";
import { TextField } from "../../../components/common/FormFields";
import { Tooltip } from "@mui/material";
import { buildNameLocalizations, findLocalizationName } from "../../../utils/soundLibraryLocalizations";
import styles from "./SoundLibraryAdmin.module.css";

interface CategoryFormValues {
    nameEN: string;
    nameFR: string;
    namePT: string;
    nameES: string;
    id?: string;
}

const CategoriesManagement: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { showNotification } = useNotification();

    const [categories, setCategories] = useState<SoundLibraryAICategoryResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string }>({ id: "", name: "" });

    // Form initial values
    const initialValues: CategoryFormValues = {
        nameEN: "",
        nameFR: "",
        namePT: "",
        nameES: "",
        id: "",
    };

    // Validation schema
    const validationSchema = Yup.object({
        nameEN: Yup.string().required(t("nameInEnglishRequired")),
        nameFR: Yup.string().required(t("nameInFrenchRequired")),
    });

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token") || "";
            const response = await getAllSoundLibraryAICategories({ token, language });
            setCategories(response || []);
        } catch (error) {
            console.error("Error fetching categories:", error);
            showNotification({
                message: t("errorFetchingCategories"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [language, t, showNotification]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleSubmit = async (
        values: CategoryFormValues,
        { resetForm, setSubmitting }: FormikHelpers<CategoryFormValues>,
    ) => {
        try {
            const token = localStorage.getItem("token") || "";
            const data = {
                name: values.nameEN,
                localizations: buildNameLocalizations(values),
            };

            if (isEditing && values.id) {
                await updateSoundLibraryAICategory({ token, id: values.id, data });
                showNotification({
                    message: t("categoryUpdatedSuccessfully"),
                    type: "success",
                });
            } else {
                await createSoundLibraryAICategory({ token, data });
                showNotification({
                    message: t("categoryCreatedSuccessfully"),
                    type: "success",
                });
            }

            fetchCategories();
            resetForm();
            setIsEditing(false);
        } catch (error) {
            console.error("Error saving category:", error);
            showNotification({
                message: isEditing ? t("errorUpdatingCategory") : t("errorCreatingCategory"),
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (
        category: SoundLibraryAICategoryResponse,
        setFieldValue: FormikHelpers<CategoryFormValues>["setFieldValue"],
        setFieldTouched: FormikHelpers<CategoryFormValues>["setFieldTouched"],
    ) => {
        setIsEditing(true);

        setFieldValue("nameEN", findLocalizationName(category.localizations, "EN") || category.name || "");
        setFieldValue("nameFR", findLocalizationName(category.localizations, "FR"));
        setFieldValue("namePT", findLocalizationName(category.localizations, "PT"));
        setFieldValue("nameES", findLocalizationName(category.localizations, "ES"));
        setFieldValue("id", category.id);
        setFieldTouched("nameEN", false);
    };

    const openDeleteModal = (category: SoundLibraryAICategoryResponse) => {
        setCategoryToDelete({
            id: category.id,
            name: category.name,
        });
        setDeleteModalOpen(true);
    };

    const handleDelete = useCallback(async () => {
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem("token") || "";
            await deleteSoundLibraryAICategory({ token, id: categoryToDelete.id });
            showNotification({
                message: t("categoryDeletedSuccessfully"),
                type: "success",
            });
            fetchCategories();
            setDeleteModalOpen(false);
        } catch (error) {
            console.error("Error deleting category:", error);
            showNotification({
                message: t("errorDeletingCategory"),
                type: "error",
            });
        } finally {
            setDeleteLoading(false);
            setIsEditing(false);
        }
    }, [categoryToDelete.id, fetchCategories, showNotification, t]);

    const sortedCategories = useMemo(() => {
        return [...categories].sort((a, b) => a.name.localeCompare(b.name));
    }, [categories]);

    return (
        <div className={styles.categoriesManagementContainer}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange
                validateOnMount
                key={i18n.language}
            >
                {({ values, isSubmitting, resetForm, setFieldValue, errors, setFieldTouched }) => (
                    <>
                        <div className={styles.makerCard}>
                            <h3 className={styles.makerTitle}>{isEditing ? t("editCategory") : t("createCategory")}</h3>
                            <Form className={`categories-form ${styles.langGrid}`}>
                                <FormGroup name="nameEN" size="small">
                                    <Field
                                        name="nameEN"
                                        component={TextField}
                                        placeholder={t("enterNameInEnglish")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="nameFR" size="small">
                                    <Field
                                        name="nameFR"
                                        component={TextField}
                                        placeholder={t("enterNameInFrench")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="namePT" size="small">
                                    <Field
                                        name="namePT"
                                        component={TextField}
                                        placeholder={t("enterNameInPortuguese")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="nameES" size="small">
                                    <Field
                                        name="nameES"
                                        component={TextField}
                                        placeholder={t("enterNameInSpanish")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <div className={styles.langActions}>
                                    {isSubmitting ? (
                                        <div
                                            style={{
                                                width: "100px",
                                                height: "46px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <div
                                                className="loading-spinner"
                                                style={{ width: "24px", height: "24px" }}
                                            ></div>
                                        </div>
                                    ) : (
                                        <Tooltip
                                            title={
                                                Object.keys(errors).length > 0
                                                    ? t("formIncompleteTooltip", {
                                                          fields: Object.values(errors).join(", "),
                                                      })
                                                    : ""
                                            }
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    label={isEditing ? t("update") : t("create")}
                                                    variant="primary"
                                                    shape="default"
                                                    className={styles.createSoundBtn}
                                                    onClick={() => {
                                                        const formElement = document.querySelector(".categories-form");
                                                        if (formElement) {
                                                            (formElement as HTMLFormElement).requestSubmit();
                                                        }
                                                    }}
                                                    disabled={isSubmitting || !values.nameEN || !values.nameFR}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isEditing && (
                                        <Button
                                            label={t("cancel")}
                                            onClick={() => {
                                                resetForm();
                                                setIsEditing(false);
                                            }}
                                            variant="cancel"
                                            disabled={isSubmitting}
                                        />
                                    )}
                                </div>
                            </Form>
                        </div>
                        <div className="categories-list-section">
                            <h3>{t("soundCategories")}</h3>

                            {loading ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                </div>
                            ) : sortedCategories.length === 0 ? (
                                <div className={styles.noItemsMessage}>{t("noCategoriesFound")}</div>
                            ) : (
                                <div className={styles.typesAndCategoriesList}>
                                    {sortedCategories.map((category) => (
                                        <div key={category.id} className={styles.typesAndCategoriesItem}>
                                            <div className={styles.typesAndCategoriesLeft}>
                                                <div className={styles.typesAndCategoriesInfo}>
                                                    <h4>{category.name}</h4>
                                                    <p>
                                                        {category.localizations?.map((loc: any) => (
                                                            <span key={loc.id} className={styles.localizationBadge}>
                                                                {loc.language}: {loc.name}
                                                            </span>
                                                        ))}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={styles.typesAndCategoriesActions}>
                                                <button
                                                    type="button"
                                                    className={styles.btnMod}
                                                    onClick={() => handleEdit(category, setFieldValue, setFieldTouched)}
                                                    disabled={isSubmitting || deleteLoading}
                                                >
                                                    {t("edit")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.btnDel}
                                                    onClick={() => openDeleteModal(category)}
                                                    disabled={isSubmitting || deleteLoading}
                                                >
                                                    {t("delete")}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Formik>
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                message={t("deleteCategoryWarning")}
                title={t("confirmDeleteCategory")}
            />
        </div>
    );
};

export default CategoriesManagement;
